/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import { Box, Snackbar } from '@material-ui/core';
import 'react-quill/dist/quill.snow.css';
import FieldWrapper from '../fieldWrapper';
import { useCommunityVersionNumber } from 'lib/hooks/use-community-version';
import Quill, {
  formatHtmlLikeTinyMCE,
  parseFontSizes,
  readLinkFormat,
  getLinkRange,
  calculateLiveLength,
} from './quill-config';
import QuillLinkDialog from './QuillLinkDialog';
import useEditorStyles from './style';

const INITIAL_LINK_DIALOG = {
  open: false, url: '', displayText: '', title: '', target: '_self', savedSelection: null,
};

const FORMATS = ['bold', 'italic', 'underline', 'size', 'list', 'bullet', 'link', 'script'];

const NAV_KEYS = new Set([
  'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
]);

export default function EditorField(props) {
  const {
    onChange,
    name,
    id,
    disabled = false,
    placeholder = '',
    fixedFont,
    isLanguageSupport,
    showBothTextTypes,
    className,
    formControlClassName,
  } = props;

  const value = props.value || '';
  const classes = useEditorStyles();

  // Refs
  const editorRef = useRef(null);
  const mountedRef = useRef(true);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const lastSelectionRef = useRef(null);
  const openLinkDialogRef = useRef(null);
  const maxLengthRef = useRef(props.maxLength);

  // Semaphore to prevent incoming prop values from resetting Quill text tracking mid-operation
  // const isUpdatingLinkRef = useRef(false);

  // State
  const [toast, setToast] = useState(false);
  const [linkDialog, setLinkDialog] = useState(INITIAL_LINK_DIALOG);

  // Custom hook
  const eventCommunityVersion = useCommunityVersionNumber();

  // Memos
  // Deeply scrub carriage returns and literal internal element structural lines
  // const quillValue = useMemo(() => {
  //   return value.replace(/\r?\n|\r/g, '').trim();
  // }, [value]);
  // Strip formatting \n before passing to Quill; parent stores TinyMCE-formatted HTML which Quill would misread as text nodes.
  const quillValue = useMemo(() => value.replace(/\n/g, ''), [value]);

  const fontSizes = useMemo(() => parseFontSizes(fixedFont), [fixedFont]);

  const modules = useMemo(() => {
    const showFontSize = eventCommunityVersion === 1;
    const toolbar = [['bold', 'italic', 'underline']];
    if (showFontSize) toolbar.push([{ size: [false, ...fontSizes] }]);
    toolbar.push([{ list: 'bullet' }, { list: 'ordered' }]);
    toolbar.push(['link']);
    toolbar.push([{ script: 'super' }, { script: 'sub' }]);
    return {
      toolbar: {
        container: toolbar,
        handlers: { link() { if (openLinkDialogRef.current) openLinkDialogRef.current(); } },
      },
      clipboard: { matchVisual: false },
    };
  }, [eventCommunityVersion, fontSizes]);

  // Effects
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  useEffect(() => { maxLengthRef.current = props.maxLength; }, [props.maxLength]);

  useEffect(() => {
    const quill = editorRef.current && editorRef.current.getEditor();
    if (!quill) return;

    const onSelectionChange = (range) => { if (range) lastSelectionRef.current = range; };
    quill.on('selection-change', onSelectionChange);

    const onLinkClick = (e) => {
      if (disabledRef.current) return;
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      e.preventDefault();

      let index = 0;
      let length = 0;
      try {
        const blot = Quill.find(anchor);
        if (!blot) return;
        index = quill.getIndex(blot);
        length = typeof blot.length === 'function' ? blot.length() : anchor.textContent.length;
      } catch (_) {
        return;
      }

      const format = quill.getFormat(index, Math.max(length, 1));
      const existingLink = readLinkFormat(format.link);
      const savedSel = { index, length };
      lastSelectionRef.current = savedSel;

      if (mountedRef.current) {
        setLinkDialog({
          open: true,
          url: existingLink ? existingLink.href : '',
          displayText: anchor.textContent || '',
          title: existingLink ? existingLink.title : '',
          target: existingLink ? (existingLink.target || '_self') : '_self',
          savedSelection: savedSel,
        });
      }
    };
    quill.root.addEventListener('click', onLinkClick);

    // Paste guard: truncate pasted content to the remaining character budget.
    const Delta = Quill.import('delta');
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      const maxLen = maxLengthRef.current;
      if (!maxLen) return delta;

      const sel = quill.getSelection();
      const selText = sel && sel.length > 0 ? quill.getText(sel.index, sel.length) : '';
      const currentLen = Math.max(0, calculateLiveLength(quill.root.innerHTML) - selText.length);
      const remaining = maxLen - currentLen;

      const pastedTextLen = delta.reduce(
        (len, op) => len + (typeof op.insert === 'string' ? op.insert.length : 0), 0,
      );

      if (remaining <= 0) {
        if (mountedRef.current) setToast(true);
        return new Delta();
      }

      if (pastedTextLen > remaining) {
        if (mountedRef.current) setToast(true);
        let count = 0;
        const limitedDelta = new Delta();
        delta.ops.forEach((op) => {
          if (count >= remaining) return;
          if (typeof op.insert === 'string') {
            const textToTake = op.insert.substring(0, remaining - count);
            limitedDelta.push({ ...op, insert: textToTake });
            count += textToTake.length;
          } else {
            limitedDelta.push(op);
          }
        });
        return limitedDelta;
      }
      return delta;
    });

    return () => {
      quill.off('selection-change', onSelectionChange);
      quill.root.removeEventListener('click', onLinkClick);
    };
  }, []);

  // Assigned each render so the toolbar link handler always closes over the latest state.
  openLinkDialogRef.current = () => {
    const quill = editorRef.current && editorRef.current.getEditor();
    if (!quill) return;

    quill.focus();
    let sel = quill.getSelection() || lastSelectionRef.current;
    const format = sel ? quill.getFormat(sel.index, Math.max(sel.length, 1)) : {};
    const existingLink = readLinkFormat(format.link);

    if (sel && sel.length === 0 && existingLink) sel = getLinkRange(quill, sel.index);

    const selectedText = sel && sel.length > 0 ? quill.getText(sel.index, sel.length) : '';

    setLinkDialog({
      open: true,
      url: existingLink ? existingLink.href : '',
      displayText: selectedText,
      title: existingLink ? existingLink.title : '',
      target: existingLink ? (existingLink.target || '_self') : '_self',
      savedSelection: sel,
    });
  };

  // Handlers
  const handleLinkSave = useCallback((linkData) => {
    const quill = editorRef.current && editorRef.current.getEditor();
    const saved = linkDialog.savedSelection;
    setLinkDialog(INITIAL_LINK_DIALOG);
    if (!quill) return;

    // Turn on lock flag to signal we are updating selection data
    // isUpdatingLinkRef.current = true;

    const sel = saved || { index: quill.getLength() - 1, length: 0 };
    quill.setSelection(sel.index, sel.length, 'silent');

    if (!linkData) {
      quill.format('link', false, 'user');
      // isUpdatingLinkRef.current = false;
      return;
    }

    const { url, displayText, title, target } = linkData;
    const linkValue = { href: url };
    if (title) linkValue.title = title;
    if (target && target !== '_self') linkValue.target = target;

    if (sel.length > 0) {
      const originalText = quill.getText(sel.index, sel.length);
      const newText = displayText || originalText;
      if (newText !== originalText) {
        quill.deleteText(sel.index, sel.length, 'api');
        quill.insertText(sel.index, newText, 'link', linkValue, 'user');
      } else {
        quill.formatText(sel.index, sel.length, 'link', linkValue, 'user');
      }
      quill.setSelection(sel.index + newText.length, 0);
    } else {
      const insertText = displayText || url;
      quill.insertText(sel.index, insertText, 'link', linkValue, 'user');
      quill.setSelection(sel.index + insertText.length, 0);
    }

    // Release layout lock safely after microtask queue resolves
    // setTimeout(() => {
    //   isUpdatingLinkRef.current = false;
    // }, 0);
  }, [linkDialog.savedSelection]);

  const handleLinkClose = useCallback(() => setLinkDialog(INITIAL_LINK_DIALOG), []);

  const handleKeyDown = useCallback((event) => {
    const maxLen = maxLengthRef.current;
    if (!maxLen) return;

    const quill = editorRef.current && editorRef.current.getEditor();
    if (!quill) return;

    const sel = quill.getSelection();
    if (sel === null) return;
    if (sel.length > 0) return;

    const plainLen = calculateLiveLength(quill.root.innerHTML);
    if (plainLen < maxLen) return;

    if (!NAV_KEYS.has(event.key) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      if (mountedRef.current) setToast(true);
    }
  }, []);

  const handleChange = useCallback((content, _delta, source) => {
    // If layout matches an API selection event and we aren't tracking link updates, reject it
    if (source === 'api') return;

    onChange({ target: { name, value: formatHtmlLikeTinyMCE(content) } });
  }, [onChange, name]);

  return (
    <FieldWrapper
      formControlClassName={`${disabled ? classes.disabled : ''} ${formControlClassName || ''}`}
      label={props.label}
      id={id}
      error={props.error}
      required={props.required}
      descType={props.descType}
      descText={props.descText}
      showBothTextTypes={showBothTextTypes}
      maxLength={props.maxLength}
      value={props.value}
    >
      {toast && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={toast}
          className={classes.snackbarBlock}
          autoHideDuration={3000}
          message="Number of characters added exceed the limit"
          onClose={() => setToast(false)}
        />
      )}

      <Box
        className={[
          classes.editorWrapper,
          disabled ? classes.disabled : '',
          className === 'calendar-description-editor' ? classes.calendarDescriptionEditor : '',
          isLanguageSupport ? classes.borderHighlightML : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={classes.editorInner} onKeyDown={handleKeyDown}>
          <ReactQuill
            ref={editorRef}
            theme="snow"
            value={quillValue}
            readOnly={disabled}
            modules={modules}
            formats={FORMATS}
            placeholder={placeholder}
            onChange={handleChange}
          />
        </div>
      </Box>

      <QuillLinkDialog
        open={linkDialog.open}
        data={{
          url: linkDialog.url,
          displayText: linkDialog.displayText,
          title: linkDialog.title,
          target: linkDialog.target,
        }}
        onSave={handleLinkSave}
        onClose={handleLinkClose}
      />
    </FieldWrapper>
  );
}